import {
	Component,
	OnInit,
	QueryList,
	ViewChildren,
	OnDestroy,
} from '@angular/core';
import { Store, select } from '@ngrx/store';
import {
	FormBuilder,
	FormControlName,
	FormControl,
	FormGroup,
} from '@angular/forms';
import { SelfDestruct } from 'src/shared/utilities/self-destruct';
import {
	takeUntil,
	map,
	filter,
	distinctUntilChanged,
	tap,
	withLatestFrom,
} from 'rxjs/operators';
import { isoCodeForCurrencyType } from 'src/shared/constants';
import * as JobDetailSelectors from './+state/job-detail.selectors';
import * as JobDetailActions from './+state/job-detail.actions';
import { JobDetails, JobDetailGeneralInformation } from './job-detail.models';
import { FormConfig } from 'src/shared/form-models/form-cva.component';
import { JobDetailPartialState } from './+state/job-detail.reducer';
import { Subject, Observable, BehaviorSubject, merge } from 'rxjs';
import { preventFlicker } from 'src/shared/utilities/custom-rxjs-operators';
import { jobToJobForm } from './job-detail.utility';
import { touchAllControls } from 'src/shared/utilities/utility-functions';
import {
	ConfirmPrevailingWageResponse,
	DateRange,
	JobStatus,
	Job,
} from 'src/shared/api';
import { slideInSlideOut } from 'src/shared/animations';
import { some, omit } from 'lodash';
import { MatDialog } from '@angular/material/dialog';
import {
	PayrollMonthlyReportArgs,
	PayrollMonthlyReportDialogComponent,
	PayrollMonthlyReportDialogData,
} from '../shared/payroll-monthly-report-dialog/payroll-monthly-report-dialog.component';
import { MatTabChangeEvent } from '@angular/material/tabs';

const jobDetailFormConfig: FormConfig<JobDetails> = {
	generalInfo: [''],
	jobLocation: [''],
	optionalJobFields: [''],
	fieldContacts: [''],
	billingDetails: [''],
	documents: [''],
	purchaseOrderDetails: [''],
	contractedRates: [''],
	prevailingWageInfo: [''],
	prevailingWageRates: [''],
};

@Component({
	templateUrl: './job-detail.component.html',
	styleUrls: ['./job-detail.component.scss'],
	animations: [slideInSlideOut],
})
export class JobDetailComponent
	extends SelfDestruct
	implements OnInit, OnDestroy
{
	@ViewChildren(FormControlName) childControls: QueryList<FormControlName>;
	private _invalidSubmission = new Subject<number>();
	form = this.fb.group(jobDetailFormConfig) as FormGroup;
	prevailingWageToggle = new FormControl(false);

	readonly job$ = this.store.pipe(
		select(JobDetailSelectors.getSelectedJob),
		filter<Job>(Boolean)
	);

	readonly currencyCode$ = merge(
		this.job$.pipe(map((job) => job.currency)),
		this.form
			.get('jobLocation')
			.valueChanges.pipe(map((jobLocation) => jobLocation?.currency))
	).pipe(
		map((currency) => isoCodeForCurrencyType(currency)),
		distinctUntilChanged()
	);

	readonly railroadId$ = this.job$.pipe(map((job) => job.railroad.id));

	readonly hasOnlyOneRate$ = this.job$.pipe(
		map((job) => (job.contractedRates || []).length === 1)
	);

	readonly id$ = this.store.pipe(select(JobDetailSelectors.getSelectedJobId));
	readonly idSubject$ = new BehaviorSubject<number>(undefined);
	readonly _updateIdSubject$ = this.id$
		.pipe(
			tap((v) => this.idSubject$.next(v)),
			takeUntil(this.destroyed)
		)
		.subscribe();

	readonly title$ = this.store.pipe(select(JobDetailSelectors.getTitle));

	readonly isNewRecord$ = this.id$.pipe(map((id) => id === 0));

	readonly isNonBillableJob$ = this.store.pipe(
		select(JobDetailSelectors.getIsNonBillableJob)
	);

	readonly isLoading$ = this.store.pipe(
		select(JobDetailSelectors.getLoadingStatus)
	);

	readonly editBillingInfo$ = this.store.pipe(
		select(JobDetailSelectors.getEditBillingInfo)
	);

	readonly isSaving$ = this.store.pipe(
		select(JobDetailSelectors.getIsSaving),
		preventFlicker(1000)
	);

	readonly subdivisionChoices$ = this.store.pipe(
		select(JobDetailSelectors.getSubdivisionOptions)
	);

	readonly hasOptionalFields$ = this.store.pipe(
		select(JobDetailSelectors.hasOptionalJobFields)
	);

	readonly railroad$ = this.store.pipe(
		select(JobDetailSelectors.getRailroadForJob)
	);

	readonly areCostCentersEnabled$ = this.store.pipe(
		select(JobDetailSelectors.areCostCentersEnabled)
	);

	readonly customer$ = this.store.pipe(
		select(JobDetailSelectors.getCustomerForJob)
	);

	readonly optionalJobFields$ = this.store.pipe(
		select(JobDetailSelectors.getOptionalJobFields)
	);

	readonly prevailingWageConfirmation$: Observable<ConfirmPrevailingWageResponse> =
		this.store.pipe(
			select(JobDetailSelectors.getPrevailingWageConfirmation)
		);

	readonly quoteLogListMessage$ = this.store.pipe(
		select(JobDetailSelectors.getQuoteLogListMessage)
	);

	readonly quoteLogs$ = this.store.pipe(
		select(JobDetailSelectors.getQuoteLogs)
	);

	readonly otherCharges$ = this.store.pipe(
		select(JobDetailSelectors.getOtherCharges)
	);

	readonly showServiceType$ = this.store.pipe(
		select(JobDetailSelectors.getShowServiceType)
	);

	readonly jobLogListMessage$ = this.store.pipe(
		select(JobDetailSelectors.getJobLogListMessage)
	);

	readonly jobLogs$ = this.store.pipe(select(JobDetailSelectors.getJobLogs));

	readonly invalidSubmission$ = this._invalidSubmission.asObservable();

	readonly JobStatus = JobStatus;

	readonly historyTabLabel = {
		job: 'Job',
		quote: 'Quote',
	};

	private tabChangeEvent$ = new BehaviorSubject<MatTabChangeEvent>(undefined);

	isFlairDocsJob = false;

	get descriptionOfSchedule() {
		return this.generalInfo.descriptionOfSchedule;
	}

	get jobIsOngoing() {
		return (
			this.generalInfo.status === JobStatus.Approved ||
			this.generalInfo.status === JobStatus.InProgress
		);
	}

	get generalInfo() {
		return ((this.form.value as JobDetails).generalInfo ||
			{}) as JobDetailGeneralInformation;
	}

	constructor(
		private readonly store: Store<JobDetailPartialState>,
		private readonly fb: FormBuilder,
		private readonly dialog: MatDialog
	) {
		super();
	}

	// TODO there's some duplicated logic here around prevailing wage
	ngOnInit() {
		this.store
			.pipe(
				select(JobDetailSelectors.getSelectedJob),
				map(jobToJobForm),
				takeUntil(this.destroyed)
			)
			.subscribe((formValue) => {
				this.form.patchValue(formValue, { emitEvent: false });
				if (
					(formValue &&
						some(omit(formValue.prevailingWageInfo, 'id'))) ||
					some(omit(formValue.prevailingWageRates, 'id'))
				) {
					this.prevailingWageToggle.setValue(true);
					if (
						formValue.prevailingWageInfo.confirmedBy ||
						formValue.prevailingWageInfo.confirmedDate
					) {
						this.prevailingWageToggle.disable();
						this.form.get('prevailingWageInfo').disable();
						this.form.get('prevailingWageRates').disable();
					}
				} else {
					this.prevailingWageToggle.setValue(false);
				}

				this.isFlairDocsJob = !!(
					formValue.generalInfo &&
					formValue.generalInfo.flairDocsJobId
				);

				if (!this.isFlairDocsJob) {
					this.form.get('contractedRates').disable();
				}
				if (
					this.form.get('billingDetails').value != null &&
					this.form.get('billingDetails').value[
						'otherBillingCompany'
					] != null
				) {
					this.store.dispatch(
						JobDetailActions.editBillingInfoToggled({
							editBillingInfo: true,
						})
					);
				}
			});

		this.prevailingWageConfirmation$
			.pipe(filter<ConfirmPrevailingWageResponse>(Boolean))
			.subscribe((confirmation) => {
				if (confirmation.confirmedBy || confirmation.confirmedDate) {
					this.prevailingWageToggle.disable();
					this.form.get('prevailingWageInfo').disable();
					this.form.get('prevailingWageRates').disable();
				}
			});

		this.tabChangeEvent$
			.pipe(
				takeUntil(this.destroyed),
				filter<MatTabChangeEvent>(Boolean),
				withLatestFrom(
					this.store.pipe(
						select(JobDetailSelectors.isQuoteLogInitialized)
					)
				),
				filter(
					([event, isInitialized]) =>
						event.tab.textLabel === this.historyTabLabel.quote &&
						!isInitialized
				)
			)
			.subscribe(() =>
				this.store.dispatch(JobDetailActions.quoteLogsRequested())
			);

		// Removing form validation for billing details on nonbillable jobs
		this.isNonBillableJob$
			.pipe(takeUntil(this.destroyed))
			.subscribe((isNonBillable) =>
				isNonBillable
					? this.form.get('billingDetails').disable()
					: this.form.get('billingDetails').enable()
			);
	}

	save(stay = false): void {
		if (this.form.valid) {
			const formValue = this.form.getRawValue();
			this.store.dispatch(
				JobDetailActions.saveRequested({
					shouldStayOnPage: stay,
					formValue,
				})
			);
		} else {
			touchAllControls(this.childControls);
			this._invalidSubmission.next(new Date().getTime());
		}
	}

	confirmPrevailingWage(): void {
		const jobForm = this.form.getRawValue() as JobDetails;
		this.store.dispatch(
			JobDetailActions.userConfirmedPrevailingWage({
				jobForm,
			})
		);
	}

	prevailingWageToggled(isChecked: boolean): void {
		if (!isChecked) {
			this.form.get('prevailingWageInfo').reset(null);
			this.form.get('prevailingWageRates').reset(null);
		}
	}

	editBillingInfoClicked(event: boolean): void {
		this.store.dispatch(
			JobDetailActions.editBillingInfoToggled({ editBillingInfo: event })
		);
	}

	updateJobDateRange(newDateRange: DateRange): void {
		this.store.dispatch(
			JobDetailActions.shiftsAddedOutsideOfJobDates({
				newDateRange,
			})
		);
	}

	cancelJob(): void {
		const jobForm = this.form.getRawValue() as JobDetails;
		this.store.dispatch(
			JobDetailActions.userConfirmedCancelJob({ jobForm })
		);
	}

	completeJob(): void {
		const jobForm = this.form.getRawValue() as JobDetails;
		this.store.dispatch(
			JobDetailActions.userConfirmedCompleteJob({
				jobForm,
			})
		);
	}

	refreshOtherCharges(): void {
		this.store.dispatch(JobDetailActions.otherChargesSaved());
	}

	requestNewJobSetupForm(): void {
		const jobForm = this.form.getRawValue() as JobDetails;
		this.store.dispatch(
			JobDetailActions.userConfirmedRequestNewJobSetupForm({ jobForm })
		);
	}

	openPayrollMonthlyReportDialog() {
		this.dialog
			.open(PayrollMonthlyReportDialogComponent, {
				width: '30rem',
				data: {
					jobId: this.idSubject$.value,
				} as PayrollMonthlyReportDialogData,
			})
			.afterClosed()
			.pipe(
				tap((args: PayrollMonthlyReportArgs) => {
					if (args) {
						this.store.dispatch(
							JobDetailActions.confirmedGeneratePayrollMonthlyReportCsv(
								{
									args,
								}
							)
						);
					}
				})
			)
			.subscribe();
	}

	handleHistorySelectedTabChange(event: MatTabChangeEvent) {
		this.tabChangeEvent$.next(event);
	}

	ngOnDestroy() {
		this.store.dispatch(JobDetailActions.jobDetailDestroyed());
		super.ngOnDestroy();
	}
}
